"use client";

import { useTranslations } from "next-intl";
import { css } from "styled-system/css";
import { stack } from "styled-system/patterns";

import { NumberFormatter } from "@/utils/number-formatter";

export interface DetailsMetricsRowProps {
  metrics: {
    locked: string;
    unlocksAt: string;
    creditsRemaining: number;
    creditsTotal: number;
    profitsKeptUsd: number | null;
  } | null;
}

export function DetailsMetricsRow({ metrics }: DetailsMetricsRowProps) {
  const t = useTranslations("firstTradeOnUs.details.metrics");
  const tGeneral = useTranslations("general");

  if (metrics === null) return null;

  const profitsValue =
    metrics.profitsKeptUsd === null
      ? tGeneral("n/a")
      : `${metrics.profitsKeptUsd > 0 ? "+" : ""}${NumberFormatter.usdc(metrics.profitsKeptUsd)}`;
  // Figma renders both N/A and positive payouts green; only a settled $0 reads neutral.
  const profitsColor =
    metrics.profitsKeptUsd === null || metrics.profitsKeptUsd > 0
      ? "text.positive"
      : "text.primary";

  return (
    <div className={rowStyles} data-testid="fft-details-metrics-row">
      <Cell label={t("locked")} value={metrics.locked} />
      <Cell label={t("unlocks")} value={metrics.unlocksAt} />
      <Cell
        label={t("trialCredits")}
        value={t("trialCreditsValue", {
          remaining: metrics.creditsRemaining,
          total: metrics.creditsTotal,
        })}
        valueColor="text.highlight"
      />
      <Cell label={t("profitsKept")} value={profitsValue} valueColor={profitsColor} />
    </div>
  );
}

interface CellProps {
  label: string;
  value: string;
  valueColor?: "text.primary" | "text.highlight" | "text.positive";
}

function Cell({ label, value, valueColor = "text.primary" }: CellProps) {
  return (
    <div className={cellStyles}>
      <span className={labelStyles}>{label}</span>
      <span className={css({ textStyle: "heading.xs", color: valueColor })}>{value}</span>
    </div>
  );
}

const rowStyles = css({
  display: "grid",
  gridTemplateColumns: {
    base: "repeat(2, minmax(0, 1fr))",
    desktop: "repeat(4, minmax(0, 1fr))",
  },
  gap: "12",
  w: "full",
  py: "12",
  px: "16",
});

const cellStyles = stack({
  gap: "4",
});

const labelStyles = css({ textStyle: "highlight.lg", color: "text.tertiary" });
